/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * Public index of Manhattan WMS case studies. Sourced from V2 Supabase
 * (case_studies WHERE domain='wms' AND is_published=true).
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';
import CaseStudyCard from '../../components/showcase/CaseStudyCard';
import ShowcaseSiblingNav from '../../components/showcase/ShowcaseSiblingNav';
import v2Supabase from '../../lib/v2Supabase';

const BrandGlows = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-32 -right-32"
      style={{
        width: '500px',
        height: '500px',
        background:
          'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)',
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-24 -left-24"
      style={{
        width: '400px',
        height: '400px',
        background:
          'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)',
      }}
    />
  </>
);

const SkeletonCard = () => (
  <div className="bg-white border border-brand-border rounded-brandCard p-6 animate-pulse">
    <div className="flex gap-1.5 mb-4">
      <div className="h-4 w-16 bg-brand-bgOff2 rounded-brandPill" />
      <div className="h-4 w-20 bg-brand-bgOff2 rounded-brandPill" />
    </div>
    <div className="h-5 bg-brand-bgOff2 rounded w-3/4 mb-3" />
    <div className="h-3 bg-brand-bgOff2 rounded w-1/2 mb-4" />
    <div className="space-y-2">
      <div className="h-3 bg-brand-bgOff2 rounded" />
      <div className="h-3 bg-brand-bgOff2 rounded w-5/6" />
      <div className="h-3 bg-brand-bgOff2 rounded w-4/6" />
    </div>
  </div>
);

const CaseStudiesIndex = () => {
  const location = useLocation();
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await v2Supabase
        .from('case_studies')
        .select('*')
        .eq('domain', 'wms')
        .eq('is_published', true)
        .order('sequence_number')
        .execute();
      if (cancelled) return;
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[CaseStudiesIndex] load failed:', error);
        setStatus('error');
        return;
      }
      setStudies(data || []);
      setStatus('ok');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
      <PublicNav />
      <BrandGlows />

      <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* Back link */}
        <div className="mt-10">
          <Link
            to="/domain/wms"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← Back to WMS
          </Link>
        </div>

        {/* Hero */}
        <header className="text-center mt-10 sm:mt-12">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            Manhattan WMS
          </p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            Case Studies
          </h1>
          <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
            22+ years of practice. Real engagements. Client identities
            anonymized — methodologies and outcomes preserved.
          </p>
        </header>

        {/* Cards */}
        <section
          aria-label="Case studies"
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
        >
          {status === 'loading' && (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          )}

          {status === 'ok' &&
            studies.map((s) => <CaseStudyCard key={s.id} study={s} />)}
        </section>

        {/* Error state */}
        {status === 'error' && (
          <div className="mt-14 text-center bg-white border border-brand-border rounded-brandCard p-8 max-w-xl mx-auto">
            <p className="text-base text-brand-text font-semibold">
              Couldn't load case studies.
            </p>
            <p className="mt-2 text-sm text-brand-muted">
              Please refresh the page and try again.
            </p>
          </div>
        )}

        {/* Empty state */}
        {status === 'ok' && studies.length === 0 && (
          <div className="mt-14 text-center bg-white border border-brand-border rounded-brandCard p-10 max-w-2xl mx-auto">
            <div className="text-4xl mb-3" aria-hidden="true">📚</div>
            <p className="text-lg text-brand-text font-semibold">
              Case studies coming soon.
            </p>
            <p className="mt-2 text-sm text-brand-muted leading-relaxed">
              The first 11 — drawn from 22+ years of Manhattan WMS practice —
              are dropping in the next two weeks.
            </p>
          </div>
        )}

        {/* Anonymization disclaimer */}
        <div className="mt-16 bg-brand-bgOff border border-brand-border rounded-brandCard p-5 max-w-3xl mx-auto">
          <p className="text-xs text-brand-muted2 leading-relaxed text-center">
            Client identities are anonymized in compliance with confidentiality
            obligations from prior engagements. Methodologies, technologies,
            and outcome metrics are preserved as authored by Lalit Ratwani.
          </p>
        </div>

        <ShowcaseSiblingNav currentPath={location.pathname} />

      </div>

      <PublicFooter />
    </div>
  );
};

export default CaseStudiesIndex;
